import { useSlideOver } from "../useSlideOver"
import { resetScrollToSection } from "../../domain/Profile/model"
import { useEffect, useState } from "react"
import { Provider } from "../../domain/Provider/schema"
import ProviderProfile from "./provider/ProviderProfile"
import { ProfileSlideOverSkeleton } from "./shared/ProfileSlideOverSkeleton"
import { PublicPractice } from "../../domain/Profile/schema"
import { DataStatus } from "../../domain/enums"
import { useCMSReady } from "../../domain/CMS/useCMSReady"
import appInsightsLogger from "../logging/appInsightsLogger"
import Spinner from "../shared/components/Spinner"
import { dataStatusesError, dataStatusesPending } from "../shared/utils"
import { scrollToElement } from "./utils/scrollToElement"
import useContentLayoutContext from "../layout/useContentLayoutContext"
import { useAppDispatch } from "../../reduxStore/hooks"

export interface ProviderProfileData {
  provider: Provider | undefined
  publicPractices: PublicPractice[] | undefined
}

export const ProviderProfileSlideOverContent: React.FC = () => {
  const dispatch = useAppDispatch()
  const { data, status, showProfileSidebar } = useProviderProfileData()
  const { scrollableContainerRef } = useContentLayoutContext()
  // preload CMS content for profile slide over and child components
  const {
    isLoading: isCmsLoading,
    isReady: isCmsReady,
    hasError: hasCmsError,
  } = useCMSReady([
    "common",
    "provider",
    "providerAgesSeen",
    "providerClientEthnicity",
    "providerCommunitiesServedMap",
    "providerDesignationsMap",
    "providerFaithOrientation",
    "providerInsuranceCarriers",
    "providerLanguagesMap",
    "providerParticipant",
    "providerQualificationsCredentialTypeMap",
    "providerQualificationsDegreeTypeMap",
    "providerStatusAdjectives",
    "providerSpecialtiesMap",
    "providerTagsMap",
    "providerTherapyTypeMap",
    "publicProfileSlide",
    "referralPacketModal",
    "referrerCodeMap",
    "requestReferralModal",
  ])

  /** Scroll to a section after CMS Content and data load. */
  useEffect(() => {
    if (isCmsLoading) return
    if (showProfileSidebar.scrollToSection && status === DataStatus.LOADED) {
      scrollToElement(
        showProfileSidebar.scrollToSection,
        scrollableContainerRef,
      )
    }
    return () => {
      if (!isCmsLoading && status === DataStatus.LOADED) {
        dispatch(resetScrollToSection())
      }
    }
  }, [
    dispatch,
    isCmsLoading,
    scrollableContainerRef,
    showProfileSidebar.scrollToSection,
    status,
  ])

  if (isCmsLoading) {
    return <Spinner />
  }

  const hasProviderPofileDataError = dataStatusesError([status])
  const hasError = hasCmsError || !isCmsReady || hasProviderPofileDataError
  if (hasError) {
    const errorMessage = hasProviderPofileDataError
      ? `ProviderProfileSlideOverContent -- Error loading Provider Profile content`
      : `ProviderProfileSlideOverContent -- Error loading CMS content`
    appInsightsLogger.traceError(errorMessage)
    throw new Error(errorMessage)
  }

  const isLoadingProfileData = dataStatusesPending([status])
  return (
    <div className="w-full h-screen flex flex-col py-2">
      {isLoadingProfileData ? (
        <ProfileSlideOverSkeleton />
      ) : (
        <ProviderProfile
          profileData={data}
          isFromSearch={!!showProfileSidebar.provider?.prefetched}
        />
      )}
    </div>
  )
}

const useProviderProfileData = () => {
  const [data, setData] = useState<ProviderProfileData>({
    provider: undefined,
    publicPractices: undefined,
  })
  const [orchestrationStatus, setOrchestrationStatus] = useState<DataStatus>(
    DataStatus.LOADING,
  )
  const {
    showProfileSidebar,
    getData,
    dataStatus: fetchStatus,
  } = useSlideOver()

  useEffect(() => {
    const fetch = async () => {
      if (!showProfileSidebar.provider) {
        return
      }

      setOrchestrationStatus(DataStatus.LOADING)
      try {
        const fetchData = await getData({
          provider: {
            id: showProfileSidebar.provider.id,
            userId: showProfileSidebar.provider.userId,
            prefetched: showProfileSidebar.provider.prefetched,
          },
        })
        setData({
          provider: fetchData?.providerProfile,
          publicPractices: fetchData?.publicPracticeProfiles,
        })

        setOrchestrationStatus(DataStatus.LOADED)
      } catch (error) {
        appInsightsLogger.traceError(`useProviderProfileData -- ${error}`)
        setOrchestrationStatus(DataStatus.ERROR)
      }
    }
    fetch()
  }, [getData, showProfileSidebar])

  const status =
    fetchStatus === DataStatus.ERROR ? DataStatus.ERROR : orchestrationStatus

  return { data, status, showProfileSidebar }
}
